// components/Modals.jsx — New Contract, Import Contract, Add Amendment, Stage Advance, Delete Confirm
import { useState, useRef } from 'react';
import { Modal, Field, Select, Callout, LoadRow, Icons } from './UI';
import { STAGES, PROC_METHODS, CONTRACT_TERMS, STAGE_NEXT_TIPS, fmtMoney, fmtDate, today } from '../utils/constants';

// ─── New Contract Modal ───────────────────────────────────────────────────
export function NewContractModal({ onClose, onCreate, profile, cms = [], apiKey, callAI }) {
  const [f, setF] = useState({
    title: '', description: '', budget: '', isIT: 'no',
    term: '1yr', termOther: '',
    procMethod: '', category: 'Professional Services', notes: '',
    assignedCmId: '',
  });
  const [docType, setDocType] = useState(null); // 'sow' | 'cmas' | null
  const [requestDoc, setRequestDoc] = useState(null); // { name, size, base64 }
  const [cmasDoc, setCmasDoc] = useState(null); // { name, size, base64 }
  const [analyzing, setAnalyzing] = useState(false);
  const [pennySuggested, setPennySuggested] = useState({});
  const [pennyReasoning, setPennyReasoning] = useState(null);
  const [showReasoning, setShowReasoning] = useState(false);
  const fileRef = useRef(null);
  const cmasFileRef = useRef(null);
  const sowFileRef = useRef(null);

  const CATEGORIES = [
    'Professional Services', 'Personal Services', 'IT Services', 'IT Goods',
    'Non-IT Goods', 'Construction', 'Consulting', 'Maintenance & Repair', 'Training', 'Other',
  ];

  function upd(k, v) { setF(p => ({ ...p, [k]: v })); }
  const ok = f.title.trim() && f.budget;

  async function handleRequestDoc(file) {
    const reader = new FileReader();
    const b64 = await new Promise((res, rej) => {
      reader.onload = () => res(reader.result.split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(file);
    });
    setRequestDoc({ name: file.name, size: file.size, base64: b64 });

    // Auto-analyze with Penny — returns description, category, type, and method
    setAnalyzing(true);
    try {
      const budgetHint = f.budget ? ` The estimated budget is $${parseFloat(f.budget).toLocaleString()}.` : '';
      const titleHint = f.title ? ` The contract title is: "${f.title}".` : '';
      const raw = await callAI(apiKey, {
        system: `You are Penny, a California state procurement assistant. Analyze the uploaded document and return ONLY a JSON object with these exact keys:
- description: 2-4 sentence professional contract description for a STD 215. Focus on services/goods, purpose, and key deliverables. No dollar amounts or dates.
- category: one of exactly: Professional Services, Personal Services, IT Services, IT Goods, Non-IT Goods, Construction, Consulting, Maintenance & Repair, Training, Other
- isIT: "yes" if IT-related, "no" if not
- procMethod: your best procurement method. Use one of these exact values: CMAS, CALNET, SLP, LPA-Other, NCB, RFQ, IFB, RFO, RFP-Primary, RFP-Secondary, Sole Source, SB/DVBE Option, F&R, PriceQuote, Emergency, UC-CSU, Exempt, Other, or empty string if unclear.

CRITICAL LPA RULE — READ FIRST: If the uploaded document is a master agreement, schedule, or ordering guide from any of these vehicles, you MUST return the matching procMethod and NEVER return empty or a competitive method:
- CMAS (California Multiple Award Schedules): GSA schedule references, CMAS contract numbers like 4-XX-XX-XXXX, "CMAS" branding, DGS CMAS program → return "CMAS"
- CALNET (California Network): CDT CALNET branding, telecom/network/internet services, CALNET contract numbers → return "CALNET"
- SLP (Software Licensing Program): DGS/OTech SLP branding, enterprise software licensing, Microsoft/Adobe/Oracle state agreements → return "SLP"
- Any other LPA, state price schedule, commodity contract, or master agreement → return "LPA-Other"
If you are unsure which LPA type but it is clearly a schedule or master agreement document, return "CMAS" as the safe default. Never return empty for an LPA document.

For regular SOW/request documents, apply SCM Vol 1 Chapter 5 method definitions:
- PriceQuote: contracts under $20K (SCM 1 §5.80.B.2.p, Rev 04/26), or F&R for IT $10K–$20K (SCM Vol 2 §1510).
- For services $20K and above, choose among IFB, RFP-Primary, RFP-Secondary, or RFO:
  - IFB: simple, routine, or standardized services requiring little discretion (e.g., pest control, equipment maintenance). Award to lowest responsible bidder. (SCM 1 §5.11)
  - RFP-Primary: complex professional services where approaches are comparable across proposers, so cost can fairly decide the award (e.g., auditing, specialized data collection). Award to lowest-cost qualified proposer. (SCM 1 §5.20)
  - RFP-Secondary: complex, unique, or creative services needing evaluation on merit, not just price (e.g., public relations/advertising, complex research, and consulting). Award to highest-scored proposer. (SCM 1 §5.25)
  - RFO: use ONLY when a qualifying multi-vendor Master Service Agreement (MSA), LPA, or GSA schedule already exists with several vendors who hold it, and a competitive mini-solicitation is needed to select among those specific pre-qualified holders for this task. Do NOT use RFO when no such master agreement exists (use IFB/RFP-Primary/RFP-Secondary instead), and do NOT use RFO for straightforward ordering against a single-vendor LPA (that's CMAS/CALNET/SLP/LPA-Other).
  - SB/DVBE Option: consider this whenever the budget is between $5,000.01 and $249,999.99 (SCM 1 §5.80.B, GC §14838.5) and the work is not construction/public works. This is a genuinely strong default candidate in this dollar range — it requires only two quotes from certified small businesses/microbusinesses OR two quotes from certified DVBEs (not mixed), has no formal advertising/bid-opening requirement, and carries no protest rights. Recommend it as the primary suggestion when the budget fits and nothing else (e.g., an existing LPA, a proprietary/sole-source situation, or a need for full open competition) points elsewhere — but note in your reasoning that it depends on being able to actually identify two qualifying vendors, which the analyst will need to confirm.
- For category "Consulting" specifically: default to RFP-Secondary (SCM 1 §5.08, Table 5.3 lists "consulting" as a Secondary RFP example) unless an existing multi-vendor MSA applies (then RFO), or the budget fits the SB/DVBE Option range above and two qualifying vendors are plausible. Note in your reasoning that RFP-Primary may fit better if the engagement is more standardized/comparable across bidders (e.g., routine audit-style consulting) — this is a genuine gray zone and should be surfaced, not silently decided.
- Also use: NCB with justification (SCM 1 §5.80.B.1), Emergency for urgent award (SCM 1 §4.07), UC-CSU for UC/CSU contracts (SCM 1 §3.06).
- reasoning: a plain-English explanation object with sub-keys: category (1-2 sentences), isIT (1 sentence), procMethod (2-3 sentences — if LPA, state clearly why), summary (1 sentence confidence level)

Return ONLY valid JSON, no explanation, no markdown.`,
        messages: [{
          role: 'user',
          content: [
            { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: b64 } },
            { type: 'text', text: `Analyze this document (it may be a SOW, scope request, CMAS schedule, or master agreement) and return the JSON.${titleHint}${budgetHint}` }
          ]
        }],
        max_tokens: 1000,
      });

      // Parse Penny's JSON response
      const clean = raw.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(clean);
      const suggested = {};

      if (parsed.description) { upd('description', parsed.description); suggested.description = true; }
      if (parsed.category) { upd('category', parsed.category); suggested.category = true; }
      if (parsed.isIT) { upd('isIT', parsed.isIT); suggested.isIT = true; }
      if (parsed.procMethod) { upd('procMethod', parsed.procMethod); suggested.procMethod = true; }
      setPennySuggested(suggested);
      if (parsed.reasoning) { setPennyReasoning(parsed.reasoning); setShowReasoning(false); }
    } catch (e) {
      console.error('Penny analysis error:', e);
    } finally {
      setAnalyzing(false);
    }
  }

  function handleCreate() {
    // When a master agreement was uploaded, it's already in cmasDoc — don't also pass it as requestDoc
    onCreate({ ...f, requestDoc: docType === 'cmas' ? null : (requestDoc || null), cmasDoc: cmasDoc || null });
  }

  return (
    <Modal title="New Contract" subtitle={`STD 215 will be pre-filled from ${profile.contactName || 'your profile'}`} onClose={onClose} size="lg">
      <div className="form-grid" style={{ gap: 16 }}>

        {/* 1. Title */}
        <Field label="Contract Title" value={f.title} onChange={v => upd('title', v)} required className="span-full" placeholder="e.g., Electrical Infrastructure Inspection Services" />

        {/* 2. Term */}
        <div className="form-field span-full">
          <label>Contract Term</label>
          <select value={f.term} onChange={e => upd('term', e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', fontSize: 13, background: 'white' }}>
            {CONTRACT_TERMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <span style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 4, display: 'block' }}>
            General length for now — exact start/end dates are captured later (STD 215).
          </span>
          {f.term === 'other' && (
            <Field value={f.termOther} onChange={v => upd('termOther', v)} className="span-full"
              placeholder="e.g., 5 years, through project completion" style={{ marginTop: 8 }} />
          )}
        </div>

        {/* 3. Budget */}
        <Field
          label={f.term === '1yr' || f.term === 'lt1yr' ? 'Budget ($)' : 'Total Contract Value — all years ($)'}
          value={f.budget} onChange={v => upd('budget', v)} type="number" required placeholder="0.00" className="span-full" />
        {f.term !== '1yr' && f.term !== 'lt1yr' && (
          <div className="span-full" style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: -10 }}>
            Enter the total across the full {CONTRACT_TERMS.find(t => t.value === f.term)?.label.toLowerCase() || 'term'}, not a single year's amount — this is what drives SB/DVBE, TACPA, and PAAL threshold checks.
          </div>
        )}

        {/* 3. Document Upload — SOW or CMAS */}
        <div className="span-full">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--slate-500)', marginBottom: 8, textAlign: 'center' }}>
            ✒ Upload a document for Penny <span style={{ fontWeight: 400 }}>— optional, but helps auto-fill fields below</span>
          </div>

          {/* Hidden file inputs — outside any button */}
          <input ref={sowFileRef} type="file" accept=".pdf" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) { setDocType('sow'); handleRequestDoc(e.target.files[0]); e.target.value = ''; }}} />
          <input ref={cmasFileRef} type="file" accept=".pdf" style={{ display: 'none' }}
            onChange={e => { const file = e.target.files[0]; if (file) { setDocType('cmas'); upd('procMethod', 'CMAS'); const r = new FileReader(); r.onload = () => { setCmasDoc({ name: file.name, size: file.size, base64: r.result.split(',')[1] }); handleRequestDoc(file); }; r.readAsDataURL(file); e.target.value = ''; }}} />

          {/* Two option tiles */}
          {!docType && !requestDoc && !cmasDoc && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxWidth: 460, margin: '0 auto' }}>
              {[
                { type: 'sow',  icon: '📄', title: 'SOW / Request Document',  desc: 'Rough SOW, scope memo, or request',   ref: sowFileRef  },
                { type: 'cmas', icon: '📋', title: 'Master Agreement / LPA',  desc: 'CMAS, CALNET, SLP, or other LPA',     ref: cmasFileRef },
              ].map(opt => (
                <div
                  key={opt.type}
                  onClick={() => opt.ref.current?.click()}
                  style={{ padding: '12px 14px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', background: 'var(--slate-50)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, userSelect: 'none' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--slate-400)'; e.currentTarget.style.background = 'white'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--slate-200)'; e.currentTarget.style.background = 'var(--slate-50)'; }}
                >
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{opt.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--navy-700)' }}>{opt.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 1 }}>{opt.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Uploaded file confirmation */}
          {(requestDoc || cmasDoc) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid var(--slate-200)', background: 'var(--slate-50)', maxWidth: 460, margin: '0 auto' }}>
              <span style={{ fontSize: 18 }}>{docType === 'cmas' ? '📋' : '📄'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--slate-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(requestDoc || cmasDoc).name}</div>
                <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 1 }}>
                  {analyzing ? '✒ Penny is reading…' : docType === 'cmas' ? '✓ Master Agreement saved — full analysis in the Penny tab' : '✓ Penny has analyzed this document'}
                </div>
              </div>
              <button onClick={() => { setRequestDoc(null); setCmasDoc(null); setDocType(null); setPennySuggested({}); setPennyReasoning(null); setShowReasoning(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--slate-400)', fontSize: 16, flexShrink: 0 }}>×</button>
            </div>
          )}
          {analyzing && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--navy-600)', fontSize: 12 }}>
              <div className="spin spin-sm" /> Penny is reading your document…
            </div>
          )}
        </div>

        {/* 4. Description */}
        <div className="form-field span-full">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Description
            {pennySuggested.description && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'var(--gold-100)', color: 'var(--gold-700)', fontWeight: 600 }}>✒ Penny</span>}
          </label>
          <textarea value={f.description} onChange={e => { upd('description', e.target.value); setPennySuggested(p => ({ ...p, description: false })); }}
            rows={3} placeholder={analyzing ? 'Penny is filling this in…' : 'Brief description of the services or goods being procured'}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (pennySuggested.description ? 'var(--gold-300)' : 'var(--slate-200)'), fontSize: 13, resize: 'vertical', fontFamily: 'inherit', background: pennySuggested.description ? 'var(--gold-50)' : 'white' }} />
        </div>

        {/* 5. Category dropdown */}
        <div className="form-field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Category
            {pennySuggested.category && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'var(--gold-100)', color: 'var(--gold-700)', fontWeight: 600 }}>✒ Penny</span>}
          </label>
          <select value={f.category} onChange={e => { upd('category', e.target.value); setPennySuggested(p => ({ ...p, category: false })); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (pennySuggested.category ? 'var(--gold-300)' : 'var(--slate-200)'), fontSize: 13, background: pennySuggested.category ? 'var(--gold-50)' : 'white' }}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* 6. Contract Type */}
        <div className="form-field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Contract Type
            {pennySuggested.isIT && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'var(--gold-100)', color: 'var(--gold-700)', fontWeight: 600 }}>✒ Penny</span>}
          </label>
          <select value={f.isIT} onChange={e => { upd('isIT', e.target.value); setPennySuggested(p => ({ ...p, isIT: false })); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (pennySuggested.isIT ? 'var(--gold-300)' : 'var(--slate-200)'), fontSize: 13, background: pennySuggested.isIT ? 'var(--gold-50)' : 'white' }}>
            <option value="no">Non-IT — SCM Vol 1</option>
            <option value="yes">IT — SCM Vol 2</option>
          </select>
        </div>

        {/* 7. Procurement Method */}
        <div className="form-field span-full">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            Procurement Method
            {pennySuggested.procMethod && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 99, background: 'var(--gold-100)', color: 'var(--gold-700)', fontWeight: 600 }}>✒ Penny</span>}
          </label>
          <select value={f.procMethod} onChange={e => { upd('procMethod', e.target.value); setPennySuggested(p => ({ ...p, procMethod: false })); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: 'var(--r-md)', border: '1.5px solid ' + (pennySuggested.procMethod ? 'var(--gold-300)' : 'var(--slate-200)'), fontSize: 13, background: pennySuggested.procMethod ? 'var(--gold-50)' : 'white' }}>
            <option value="">Select method…</option>
            {PROC_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          {pennySuggested.procMethod && (
            <span style={{ fontSize: 11, color: 'var(--gold-600)', marginTop: 4, display: 'block' }}>
              ✒ Penny's suggestion — verify before proceeding
            </span>
          )}
        </div>

        {/* Penny Reasoning Panel */}
        {pennyReasoning && (
          <div className="form-field span-full" style={{
            borderRadius: 'var(--r-lg)',
            border: '1.5px solid var(--gold-200)',
            overflow: 'hidden',
            background: 'var(--gold-50)',
          }}>
            <button
              onClick={() => setShowReasoning(p => !p)}
              style={{
                width: '100%', padding: '10px 16px',
                background: 'transparent', border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{ fontSize: 15 }}>✒</span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--gold-800)' }}>
                  Why Penny suggested this
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--gold-600)' }}>
                  {pennyReasoning.summary || "Click to see Penny's reasoning for each field"}
                </div>
              </div>
              <span style={{ color: 'var(--gold-500)', fontSize: 12 }}>{showReasoning ? '▲' : '▼'}</span>
            </button>

            {showReasoning && (
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--gold-200)' }}>
                {[
                  { label: 'Category', key: 'category', icon: '🗂' },
                  { label: 'Contract Type', key: 'isIT', icon: '💻' },
                  { label: 'Procurement Method', key: 'procMethod', icon: '📋' },
                ].map(({ label, key, icon }) => pennyReasoning[key] && (
                  <div key={key} style={{ marginTop: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy-700)', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{icon}</span> {label}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--slate-600)', lineHeight: 1.6 }}>
                      {pennyReasoning[key]}
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--gold-200)', fontSize: 11.5, color: 'var(--gold-600)', fontStyle: 'italic' }}>
                  All fields are editable — Penny's suggestions are a starting point, not a final determination.
                </div>
              </div>
            )}
          </div>
        )}

        <Field label="Notes" value={f.notes} onChange={v => upd('notes', v)} rows={2} className="span-full" placeholder="Any initial notes or context" />

        {cms.filter(c => c.active !== false).length > 0 && (
          <div className="form-field span-full">
            <label>Assign State Contract Manager</label>
            <select value={f.assignedCmId} onChange={e => upd('assignedCmId', e.target.value)}>
              <option value="">— Assign later —</option>
              {cms.filter(c => c.active !== false).map(cm => (
                <option key={cm.id} value={cm.id}>{cm.name}{cm.title ? ' — ' + cm.title : ''}</option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>Selected CM info will auto-fill STD 215 contact fields</span>
          </div>
        )}
      </div>
      <div className="btn-row">
        <button className="btn btn-gold" disabled={!ok || analyzing} onClick={handleCreate}>
          <Icons.plus /> Create Contract
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ─── Import Contract Modal ────────────────────────────────────────────────
export function ImportContractModal({ onClose, onImport, loading }) {
  const [files, setFiles] = useState([]);
  const [stage, setStage] = useState(6);
  const [notes, setNotes] = useState('');
  const fileRef = useRef(null);

  function addFiles(newFiles) {
    setFiles(p => [...p, ...Array.from(newFiles)]);
  }

  return (
    <Modal title="Import Existing Contract" subtitle="Upload PDFs — Penny will extract key fields automatically" onClose={onClose} size="lg">
      <div
        style={{
          border: '2px dashed var(--slate-300)', borderRadius: 'var(--r-lg)',
          padding: '24px', textAlign: 'center', cursor: 'pointer',
          background: 'var(--slate-50)', marginBottom: 16,
          transition: 'all 0.15s',
        }}
        onClick={() => fileRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
      >
        <div style={{ fontSize: 24, marginBottom: 8, opacity: .6 }}>📄</div>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--slate-700)', marginBottom: 4 }}>
          {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Click or drag PDFs here'}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--slate-400)' }}>
          SOW, solicitation, existing STD 215, or any contract document
        </div>
        <input ref={fileRef} type="file" multiple accept=".pdf" style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)} />
      </div>

      {files.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {files.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
              background: 'var(--slate-50)', borderRadius: 'var(--r-md)', marginBottom: 4,
            }}>
              <Icons.file />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5 }}>{f.name}</span>
              <span style={{ fontSize: 11, color: 'var(--slate-400)' }}>{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => setFiles(p => p.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red-500)', display: 'flex' }}>
                <Icons.x />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>
          Current Stage
        </label>
        <div className="stage-picker">
          {STAGES.map((s, i) => (
            <div key={i} className={`stage-pick-btn ${stage === i ? 'active' : ''}`} onClick={() => setStage(i)}>
              {i + 1}. {s.short}
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: 'var(--slate-400)', marginTop: 6 }}>
          Selected: Stage {stage + 1} — {STAGES[stage].label}
        </p>
      </div>

      <Field label="Additional context (optional)" value={notes} onChange={setNotes} rows={2}
        placeholder="e.g., IT contract for data migration, budget was recently amended…" />

      <Callout type="blue" style={{ marginTop: 12 }}>
        Penny will extract: contract title, budget, dates, contractor name, procurement method, and agreement number. Max 100 pages per PDF.
      </Callout>

      <div className="btn-row">
        <button className="btn btn-gold" disabled={files.length === 0 || loading} onClick={() => onImport(files, stage, notes)}>
          {loading ? <><div className="spin spin-sm" /> Importing…</> : <><Icons.upload /> Import Contract</>}
        </button>
        <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
        {loading && <span style={{ fontSize: 11.5, color: 'var(--slate-400)', alignSelf: 'center' }}>Usually 15–30 seconds…</span>}
      </div>
    </Modal>
  );
}

// ─── Stage Advance Modal ──────────────────────────────────────────────────
export function StageModal({ contract, pendingStage, onConfirm, onClose, dgsResult }) {
  const [note, setNote] = useState('');
  const isAdvance = pendingStage > contract.stage;
  const targetStage = STAGES[pendingStage];

  // Stages that require a note before advancing
  const requiresNote = [5, 9, 10, 12, 13, 14].includes(pendingStage) && isAdvance;
  const notePlaceholders = {
    5:  'e.g., Solicitation reviewed and approved by Legal — cleared for posting',
    9:  'e.g., Evaluation panel scored all proposals — XYZ Corp ranked highest at 87/100',
    10: 'e.g., Award notification sent to XYZ Corp on [date] — union notification letter mailed',
    12: 'e.g., No protests received — protest period closed [date]',
    13: 'e.g., Contract signed by both parties — effective date [date]',
    14: 'e.g., DGS/OLS routing package submitted — approval received [date]',
  };

  const warnings = [];
  if (pendingStage === 10 && !contract.s215?.unionNotified) {
    warnings.push('Union notification required per GC 19131 before advancing to this stage.');
  }
  if (pendingStage === 13 && dgsResult?.required) {
    warnings.push('DGS/OLS routing is required above your PAAL threshold. Ensure routing package is complete.');
  }
  if (pendingStage === 14 && !contract.s215?.signatoryName) {
    warnings.push('Authorized State Official signature not yet recorded in STD 215.');
  }

  const canConfirm = !requiresNote || note.trim().length > 0;

  return (
    <Modal
      title={isAdvance ? 'Advance to Stage ' + (pendingStage + 1) : 'Back to Stage ' + (pendingStage + 1)}
      subtitle={targetStage?.label}
      onClose={onClose}
    >
      {warnings.map((w, i) => (
        <Callout key={i} type="orange" style={{ marginBottom: 8 }}>{w}</Callout>
      ))}
      {STAGE_NEXT_TIPS[pendingStage] && (
        <Callout type="blue" style={{ marginBottom: 12 }}>
          <strong>Tip:</strong> {STAGE_NEXT_TIPS[pendingStage]}
        </Callout>
      )}

      {/* Stage note */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: requiresNote ? 'var(--navy-700)' : 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 6 }}>
          Stage Note {requiresNote ? <span style={{ color: 'var(--red-500)' }}>* Required</span> : <span style={{ fontWeight: 400 }}>(optional)</span>}
        </label>
        {requiresNote && (
          <p style={{ fontSize: 12, color: 'var(--slate-500)', marginBottom: 8, lineHeight: 1.5 }}>
            A note is required at this stage for the procurement audit trail. Document the key decision or action taken.
          </p>
        )}
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder={notePlaceholders[pendingStage] || 'Reason for stage change, decision notes, approvals received…'}
          rows={3}
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 13,
            border: '1.5px solid ' + (requiresNote && !note.trim() ? 'var(--orange-300)' : 'var(--slate-200)'),
            resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
          }}
        />
        {requiresNote && !note.trim() && (
          <p style={{ fontSize: 11.5, color: 'var(--orange-600)', marginTop: 4 }}>
            Please add a note before advancing.
          </p>
        )}
      </div>

      <div className="btn-row">
        <button className="btn btn-gold" disabled={!canConfirm} onClick={() => onConfirm(pendingStage, note)}>
          <Icons.check /> Confirm
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────
export function DeleteConfirmModal({ contract, onConfirm, onClose }) {
  return (
    <Modal title="Delete Contract?" onClose={onClose}>
      <Callout type="red" style={{ marginBottom: 16 }}>
        This will permanently delete <strong>{contract?.title || 'this contract'}</strong> and all associated documents, attachments, and history. This cannot be undone.
      </Callout>
      <div className="btn-row">
        <button className="btn btn-danger" onClick={onConfirm}>
          <Icons.trash /> Delete Permanently
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

// ─── Add Amendment Modal ──────────────────────────────────────────────────
export function AddAmendmentModal({ contract, onAdd, onClose }) {
  const s215 = contract.s215 || {};
  const currentMax = parseFloat(s215.maxAmount || contract.budget) || 0;
  const amendNum = (contract.amendments?.length || 0) + 1;

  const [f, setF] = useState({
    description: '',
    date: today(),
    amountChange: '0',
    newMaxAmount: String(currentMax),
    reason: '',
    agreementNum: (s215.agreementNum || '') + `-A${amendNum}`,
  });

  function upd(k, v) {
    setF(p => {
      const n = { ...p, [k]: v };
      if (k === 'amountChange') n.newMaxAmount = String(Math.max(0, currentMax + (parseFloat(v) || 0)));
      if (k === 'newMaxAmount') n.amountChange = String((parseFloat(v) || 0) - currentMax);
      return n;
    });
  }

  const amtChg = parseFloat(f.amountChange) || 0;
  const newMax = parseFloat(f.newMaxAmount) || 0;

  return (
    <Modal title={`Add Amendment ${amendNum}`} subtitle={`Amendment to ${contract.title || 'this contract'}`} onClose={onClose} size="lg">
      <div className="form-grid">
        <Field label="Description" value={f.description} onChange={v => upd('description', v)} rows={3} required className="span-full"
          placeholder="Describe the nature and scope of this amendment" />
        <Field label="Effective Date" value={f.date} onChange={v => upd('date', v)} type="date" />
        <Field label="Amendment Agreement Number" value={f.agreementNum} onChange={v => upd('agreementNum', v)} monospace />
        <Field label="Amount Change ($)" value={f.amountChange} onChange={v => upd('amountChange', v)} type="number"
          hint="Positive to increase, negative to decrease" />
        <Field label="New Maximum Amount ($)" value={f.newMaxAmount} onChange={v => upd('newMaxAmount', v)} type="number" />
        <Field label="Justification / Reason" value={f.reason} onChange={v => upd('reason', v)} rows={2} className="span-full"
          placeholder="Basis for the amendment per SCM requirements" />
      </div>

      <div style={{
        background: 'var(--slate-50)', borderRadius: 'var(--r-md)', padding: '12px 16px',
        margin: '16px 0', fontSize: 12.5, display: 'flex', gap: 16, flexWrap: 'wrap',
        border: '1px solid var(--slate-200)',
      }}>
        <div>
          <span style={{ color: 'var(--slate-500)' }}>Original max: </span>
          <strong>{currentMax ? fmtMoney(currentMax) : 'TBD'}</strong>
        </div>
        <div>→</div>
        <div>
          <span style={{ color: 'var(--slate-500)' }}>New max: </span>
          <strong style={{ color: amtChg > 0 ? 'var(--green-600)' : amtChg < 0 ? 'var(--red-600)' : 'inherit' }}>
            {newMax ? fmtMoney(newMax) : 'TBD'}
          </strong>
          {amtChg !== 0 && (
            <span style={{ marginLeft: 6, fontSize: 11, color: amtChg > 0 ? 'var(--green-600)' : 'var(--red-600)' }}>
              ({amtChg > 0 ? '+' : ''}{fmtMoney(amtChg)})
            </span>
          )}
        </div>
      </div>

      <div className="btn-row">
        <button className="btn btn-gold" disabled={!f.description.trim()} onClick={() => onAdd(f)}>
          <Icons.check /> Add Amendment
        </button>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}
